import { post } from './api';
import { logRegistrationFailure, rememberRegistrationAttempt, type RegistrationStage } from './passkey-registration';
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';

export async function quickSignInCapabilities() {
  const secureContext = window.isSecureContext;
  const publicKeyCredentialAvailable = typeof PublicKeyCredential !== 'undefined';
  const credentialsCreateAvailable = typeof navigator !== 'undefined' && typeof navigator.credentials?.create === 'function';
  let platformAuthenticatorAvailable = false;
  try {
    platformAuthenticatorAvailable = secureContext && publicKeyCredentialAvailable
      && typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
      && await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch { /* Capability failure is not an account/authentication failure. */ }
  return { secureContext, publicKeyCredentialAvailable, credentialsCreateAvailable, platformAuthenticatorAvailable };
}
export async function supportsQuickSignIn(): Promise<boolean> {
  // Authentication can use any discoverable credential provider, not only a local platform authenticator.
  return quickSignInDecision().eligible;
}
export function quickSignInDecision() {
  const secureContext = window.isSecureContext;
  const hasPublicKeyCredential = typeof PublicKeyCredential !== 'undefined';
  const hasCredentialsGet = typeof navigator !== 'undefined' && typeof navigator.credentials?.get === 'function';
  const reason = !secureContext ? 'insecure context' : !hasPublicKeyCredential ? 'PublicKeyCredential missing'
    : !hasCredentialsGet ? 'navigator.credentials.get missing' : 'not hidden (capability eligible)';
  return { secureContext, hasPublicKeyCredential, hasCredentialsGet,
    eligible: secureContext && hasPublicKeyCredential && hasCredentialsGet, reason };
}
export async function supportsQuickSignInSetup(): Promise<boolean> {
  const capability = await quickSignInCapabilities();
  return capability.secureContext && capability.publicKeyCredentialAvailable
    && capability.credentialsCreateAvailable && capability.platformAuthenticatorAvailable;
}

export async function registerQuickSignIn() {
  let stage: RegistrationStage = 'library';
  let credentialId: string | undefined;
  try {
    const { startRegistration } = await import('@simplewebauthn/browser');
    stage = 'options';
    const { options, challengeId } = await post<{ options: PublicKeyCredentialCreationOptionsJSON; challengeId: string }>(
      '/api/student/passkeys/register/options', {});
    stage = 'authenticator';
    const response = await startRegistration({ optionsJSON: options });
    credentialId = response.id;
    stage = 'verification';
    await post('/api/student/passkeys/register/verify', { challengeId, response });
  } catch (error) { rememberRegistrationAttempt(error, stage, credentialId); logRegistrationFailure(stage, error); throw error; }
}

export async function quickSignIn(challenge: string, method: 'qr' | 'code') {
  const [{ options, challengeId }, { startAuthentication }] = await Promise.all([
    post<{ options: PublicKeyCredentialRequestOptionsJSON; challengeId: string }>('/api/student/passkeys/authenticate/options', { challenge, method }),
    import('@simplewebauthn/browser'),
  ]);
  const response = await startAuthentication({ optionsJSON: options });
  const result = await post<{ returnTo: string }>('/api/student/passkeys/authenticate/verify', { challengeId, response });
  return result;
}

export function safeStudentReturn(path: string | undefined) {
  return path === '/me' || path?.startsWith('/check-in?') ? path : '/me';
}
